/**
 * API client.
 *
 * The access token lives in memory only; the refresh token is an httpOnly
 * cookie the browser sends to /api/auth on its own. A 401 triggers one silent
 * refresh, and concurrent 401s share that single refresh instead of stampeding.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Array<{ path: string; message: string }>;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-level messages, keyed by form field name. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) out[detail.path] = detail.message;
    return out;
  }
}

let accessToken: string | null = null;
let onUnauthenticated: ((reason?: string) => void) | null = null;
let refreshInFlight: Promise<boolean> | null = null;
/**
 * Why the last refresh failed, when the server said something worth repeating.
 *
 * An expired session and a session signed out for sitting idle both end up
 * here, and the difference is the whole point of the second one: somebody who
 * is told "you were signed out for being idle" knows nothing is wrong.
 */
let lastRefreshFailure: string | undefined;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;
export const setUnauthenticatedHandler = (handler: (reason?: string) => void): void => {
  onUnauthenticated = handler;
};

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        const failure = (await res.json().catch(() => null)) as {
          error?: { code?: string };
        } | null;
        lastRefreshFailure = failure?.error?.code;
        return false;
      }
      lastRefreshFailure = undefined;
      const body = (await res.json()) as { data: { accessToken: string } };
      accessToken = body.data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  raw?: boolean;
}

async function call<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const { method = 'GET', body, signal, raw } = options;

  const res = await fetch(path, {
    method,
    credentials: 'include',
    signal,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401 && !isRetry && !path.startsWith('/api/auth/')) {
    // Cloned before anything reads the body, so the reason survives whichever
    // of this request and the refresh noticed the sign-out first.
    const refused = (await res
      .clone()
      .json()
      .catch(() => null)) as { error?: { code?: string } } | null;

    const refreshed = await refreshSession();
    if (refreshed) return call<T>(path, options, true);
    accessToken = null;
    onUnauthenticated?.(refused?.error?.code ?? lastRefreshFailure);
  }

  if (res.status === 204) return undefined as T;
  if (raw) return (await res.text()) as T;

  const payload = (await res.json().catch(() => null)) as {
    data?: T;
    error?: { code: string; message: string; details?: Array<{ path: string; message: string }> };
  } | null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload?.error?.code ?? 'UNKNOWN',
      payload?.error?.message ?? `Request failed (${res.status})`,
      payload?.error?.details,
    );
  }
  return (payload?.data ?? (payload as T)) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => call<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => call<T>(path, { method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => call<T>(path, { method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => call<T>(path, { method: 'PUT', body: body ?? {} }),
  delete: <T>(path: string) => call<T>(path, { method: 'DELETE' }),
  refresh: refreshSession,
};

/** Streams a CSV/JSON export to the browser as a download. */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'Could not generate that export');

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const qs = (
  params: Record<string, string | number | boolean | undefined | null>,
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * The field-by-field reasons behind a refused write.
 *
 * The API answers a validation failure with "Check the highlighted fields" and
 * a list of paths. Without this the message is a lie: nothing is highlighted,
 * and somebody who typed one character wrong in an IFSC has no way to find it.
 */
export function fieldErrors(caught: unknown): Record<string, string> {
  if (!(caught instanceof ApiError) || !Array.isArray(caught.details)) return {};
  const mapped: Record<string, string> = {};
  for (const detail of caught.details) {
    // The first reason for a field is the one worth showing.
    if (!(detail.path in mapped)) mapped[detail.path] = detail.message;
  }
  return mapped;
}
